import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiError } from '../../abstract/api-error';
import { AdminInvitationService } from '../../core/data-services/admin-invitation.service';
import { OrganizationService } from '../../core/data-services/organization.service';
import { ProfileInvitationRequest } from '../../core/request-models/profile-invitation-request.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import { firstValueFrom } from 'rxjs';

/**
 * Create User Component
 *
 * Creates a new user profile with temp ID and optionally sends an invitation.
 * The user will complete their account setup (password) via the invitation link.
 *
 * Admin can:
 * - Create profile with email, first name, last name
 * - Create a new company or assign to an existing organization
 * - Choose invitation type (company_owner, pilot, admin)
 * - Send invitation immediately or save without inviting
 */
@Component({
  selector: 'app-create-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss'
})
export class CreateUserComponent implements OnInit {
  createForm: FormGroup;
  loading = false;
  loadingOrganizations = false;
  errorMessage = '';
  organizations: OrganizationOption[] = [];

  /** Organization mode: 'new' for creating new org, 'existing' for adding to existing */
  organizationMode: 'new' | 'existing' = 'new';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private invitationService: AdminInvitationService,
    private organizationService: OrganizationService
  ) {
    this.createForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      companyName: [''], // For creating a new company
      organizationId: [''], // For selecting existing organization
      invitationType: ['company_owner', Validators.required],
      sendInvitation: [true] // Send invitation immediately after creation
    });

    // Watch for sendInvitation changes to update validation
    this.createForm.get('sendInvitation')?.valueChanges.subscribe(() => {
      this.updateCompanyValidation();
    });
  }

  /**
   * Set the organization mode and reset relevant form fields
   */
  setOrganizationMode(mode: 'new' | 'existing'): void {
    this.organizationMode = mode;

    if (mode === 'new') {
      // Clear existing org selection, set role to owner
      this.createForm.get('organizationId')?.setValue('', { emitEvent: false });
      this.createForm.get('invitationType')?.setValue('company_owner', { emitEvent: false });
    } else {
      // Clear new company name, set role to pilot
      this.createForm.get('companyName')?.setValue('', { emitEvent: false });
      this.createForm.get('invitationType')?.setValue('pilot', { emitEvent: false });
    }

    this.updateCompanyValidation();
  }

  /**
   * Update validation for company/organization fields based on form state
   */
  private updateCompanyValidation(): void {
    const sendInvitation = this.createForm.get('sendInvitation')?.value;
    const companyName = this.createForm.get('companyName')?.value?.trim();
    const organizationId = this.createForm.get('organizationId')?.value;

    // If sending invitation, either companyName OR organizationId must be provided
    // Custom validator will handle this in the template
    // For now, we just ensure the form knows about the state
    this.createForm.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Check if company/organization is valid (for form validation display)
   */
  get isCompanyOrOrgValid(): boolean {
    const sendInvitation = this.createForm.get('sendInvitation')?.value;
    if (!sendInvitation) return true;

    if (this.organizationMode === 'new') {
      const companyName = this.createForm.get('companyName')?.value?.trim();
      return !!companyName;
    } else {
      const organizationId = this.createForm.get('organizationId')?.value;
      return !!organizationId;
    }
  }

  async ngOnInit(): Promise<void> {
    await this.loadOrganizations();
  }

  /**
   * Load organizations from API
   */
  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;

    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
    } catch (error) {
      console.error('Failed to load organizations:', error);
      this.organizations = [];
    } finally {
      this.loadingOrganizations = false;
    }
  }

  async onSubmit(): Promise<void> {
    // Check basic form validity
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const formData = this.createForm.value;

    // Check company/org validation for invitations
    if (formData.sendInvitation && !this.isCompanyOrOrgValid) {
      this.errorMessage = 'Please enter a company name or select an existing organization';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      if (formData.sendInvitation) {
        // Build invitation data
        const invitationData: ProfileInvitationRequest = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          invitationType: formData.invitationType
        };

        // Include either new company name OR existing organization ID
        if (formData.companyName && formData.companyName.trim()) {
          invitationData.companyName = formData.companyName.trim();
        } else if (formData.organizationId) {
          invitationData.organizationId = formData.organizationId;
        }

        await firstValueFrom(this.invitationService.createInvitation(invitationData));

        // Get organization name for success page
        const orgName = formData.companyName?.trim() ||
          this.organizations.find(o => o.id === formData.organizationId)?.name || '';

        // Navigate to success page
        this.router.navigate(['/success'], {
          state: {
            entities: [
              {
                type: 'user',
                action: 'created',
                name: `${formData.firstName} ${formData.lastName}`,
              },
            ],
            message: 'User created and invitation sent successfully',
            details: {
              'Email': formData.email,
              'Role': this.getInvitationTypeLabel(formData.invitationType),
              'Organization': orgName
            },
            createAnotherRoute: '/users/create'
          }
        });
      } else {
        // Create profile without sending invitation
        await firstValueFrom(this.invitationService.createInvitation({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName?.trim() || undefined,
          organizationId: formData.organizationId || undefined,
          invitationType: formData.invitationType,
          skipEmail: true // Tell API not to send invitation email
        }));

        // Get organization name for success page
        const orgName = formData.companyName?.trim() ||
          this.organizations.find(o => o.id === formData.organizationId)?.name || '';

        // Navigate to success page
        this.router.navigate(['/success'], {
          state: {
            entities: [
              {
                type: 'user',
                action: 'created',
                name: `${formData.firstName} ${formData.lastName}`,
              },
            ],
            message: 'User created successfully (invitation not sent)',
            details: {
              'Email': formData.email,
              'Role': this.getInvitationTypeLabel(formData.invitationType),
              'Organization': orgName || 'N/A'
            },
            createAnotherRoute: '/users/create'
          }
        });
      }
    } catch (err: unknown) {
      console.error('Create user error:', err);
      const error = ApiError.fromHttpError(err);

      if (error.status === 409) {
        this.errorMessage = 'A user with this email address already exists with a Firebase account';
      } else if (error.status === 400) {
        this.errorMessage = error.errors[0] ?? 'Invalid user data';
      } else {
        this.errorMessage = 'Failed to create user. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/users']);
  }

  /**
   * Get available invitation types based on organization mode
   * - New organization: only company_owner (they must be the owner)
   * - Existing org: pilot, pic, or chief_supervisor (can't add another owner)
   */
  get invitationTypes() {
    if (this.organizationMode === 'new') {
      return [
        { value: 'company_owner', label: 'Company Owner' }
      ];
    }
    // Existing organization - can't be owner
    return [
      { value: 'pilot', label: 'Pilot' },
      { value: 'pic', label: 'Pilot in Command' },
      { value: 'chief_supervisor', label: 'Chief Supervisor' }
    ];
  }

  /**
   * Get display label for invitation type
   */
  private getInvitationTypeLabel(invitationType: string): string {
    switch (invitationType) {
      case 'company_owner':
        return 'Company Owner';
      case 'pilot':
        return 'Pilot';
      case 'pic':
        return 'Pilot in Command';
      case 'chief_supervisor':
        return 'Chief Supervisor';
      default:
        return 'Member';
    }
  }
}
